import { ExternalLink } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatCurrency, formatDateTime, formatDuration, formatRelativeDate } from '@/lib/formatters'
import { type GeicoQaFixture, GeicoQaRunStatus } from '@/models/geicoQa'
import { GeicoQaVerdict } from './GeicoQaVerdict'

interface GeicoQaFixtureRowProps {
  fixture: GeicoQaFixture
  onRun: (slug: string) => void
  isStarting: boolean
}

export function GeicoQaFixtureRow({ fixture, onRun, isStarting }: GeicoQaFixtureRowProps) {
  const run = fixture.latestRun
  const isRunning = run?.status === GeicoQaRunStatus.Pending

  return (
    <TableRow>
      <TableCell className="align-top whitespace-normal">
        <div className="font-medium">{fixture.name}</div>
        {fixture.description && <div className="text-muted-foreground text-sm">{fixture.description}</div>}
        <div className="text-muted-foreground font-mono text-xs">{fixture.slug}</div>
      </TableCell>

      <TableCell className="align-top whitespace-normal">
        <GeicoQaVerdict run={run} />
        {run?.failureReason && <div className="mt-1 text-sm text-red-600">{run.failureReason}</div>}
        {run?.error && !run.failureReason && (
          <div className="text-muted-foreground mt-1 line-clamp-3 text-sm">{run.error}</div>
        )}
      </TableCell>

      <TableCell className="align-top">
        {run?.premiumAnnual ? (
          <>
            <div>{formatCurrency(Number(run.premiumAnnual))}/yr</div>
            {run.premium6Month && (
              <div className="text-muted-foreground text-xs">
                {formatCurrency(Number(run.premium6Month))} / 6 mo
              </div>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>

      <TableCell className="text-muted-foreground align-top text-sm">
        {run ? (
          <>
            {/* Exact time on hover: two runs an hour apart both read "Today". */}
            <div title={formatDateTime(run.dateCreated)}>{formatRelativeDate(run.dateCreated)}</div>
            {formatDuration(run.durationSeconds) && (
              <div className="text-xs">took {formatDuration(run.durationSeconds)}</div>
            )}
          </>
        ) : (
          '—'
        )}
      </TableCell>

      <TableCell className="align-top">
        <div className="flex flex-col items-start gap-1">
          {run?.dashboardUrl && (
            <a
              className="flex items-center gap-1 text-sm underline"
              href={run.dashboardUrl}
              rel="noreferrer"
              target="_blank"
            >
              GEICO quote <ExternalLink className="size-3" />
            </a>
          )}
          {run?.runUrl && (
            <a
              className="flex items-center gap-1 text-sm underline"
              href={run.runUrl}
              rel="noreferrer"
              target="_blank"
            >
              Supergood run <ExternalLink className="size-3" />
            </a>
          )}
          {run?.retentionId && (
            <span className="text-muted-foreground font-mono text-xs">{run.retentionId}</span>
          )}
        </div>
      </TableCell>

      <TableCell className="text-right align-top">
        <Button
          disabled={isRunning}
          loading={isStarting ? 'Starting…' : undefined}
          onClick={() => onRun(fixture.slug)}
          size="sm"
          variant="outline"
        >
          {run ? 'Run again' : 'Run'}
        </Button>
      </TableCell>
    </TableRow>
  )
}
